import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useState,
  type PropsWithChildren,
} from 'react';
import {
  customerLocationOptions,
  geolocationErrorStatus,
  toCustomerLocation,
  type CustomerLocationState,
} from './customerLocation';

const idleState: CustomerLocationState = { status: 'idle' };

/**
 * Browser-only, memory-only customer location. Never persist or send its result.
 */
export function useCustomerLocation() {
  const [state, setState] = useState<CustomerLocationState>(idleState);

  const locate = useCallback((options: PositionOptions) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setState({ status: 'unsupported' });
      return;
    }

    setState({ status: 'requesting' });
    navigator.geolocation.getCurrentPosition(
      (position) => setState({ status: 'granted', location: toCustomerLocation(position) }),
      (error) => setState({ status: geolocationErrorStatus(error) }),
      options
    );
  }, []);

  const requestLocation = useCallback(() => locate(customerLocationOptions), [locate]);

  // A customer-requested refresh must not reuse the five-minute browser cache.
  const refreshLocation = useCallback(
    () => locate({ ...customerLocationOptions, maximumAge: 0 }),
    [locate]
  );

  const resetLocation = useCallback(() => setState(idleState), []);

  return { ...state, requestLocation, refreshLocation, resetLocation };
}

type CustomerLocationContextValue = ReturnType<typeof useCustomerLocation>;

const CustomerLocationContext = createContext<CustomerLocationContextValue | null>(null);

/**
 * Shares one browser-geolocation request/result across Store discovery surfaces.
 * The value is intentionally React-memory only.
 */
export function CustomerLocationProvider({ children }: PropsWithChildren) {
  const location = useCustomerLocation();
  return createElement(CustomerLocationContext.Provider, { value: location }, children);
}

export function useSharedCustomerLocation(): CustomerLocationContextValue {
  const location = useContext(CustomerLocationContext);
  if (!location)
    throw new Error('useSharedCustomerLocation must be used within CustomerLocationProvider.');
  return location;
}
